/**
 * Rebuilding each game from the GAME_STATE_UPDATED messages one socket receives.
 *
 * The server sends a socket the whole game first, then only the changes since
 * the last message it sent that socket. This keeps, per game, the state as of
 * the last message, and turns every message back into the whole game.
 *
 * The state is kept as a JSON string, so nothing the app does to the games it
 * is handed can change what the next patch is applied to.
 *
 * Socket-free; `onGameStateUpdated` (shared-react-hooks) wires it to a socket.
 */
import { applyJsonPatch } from './jsonPatch';
/** How long to wait for the whole game before asking for it again. */
export const GAME_STATE_RESYNC_RETRY_MS = 5000;
export class GameStateReceiver {
    constructor(now = Date.now) {
        this.now = now;
        this.games = new Map();
    }
    receive(message) {
        const game = this.rebuild(message);
        if (game) {
            const { roomCode, source, timestamp } = message;
            return { update: { roomCode, game, source, timestamp } };
        }
        const stream = this.games.get(message.gameId);
        if (stream && 'awaitingSince' in stream && this.now() - stream.awaitingSince < GAME_STATE_RESYNC_RETRY_MS) {
            return null;
        }
        this.games.set(message.gameId, { awaitingSince: this.now() });
        return { resync: { roomCode: message.roomCode, gameId: message.gameId } };
    }
    /** Forget every game: the connection dropped, and the server starts each one over. */
    reset() {
        this.games.clear();
    }
    /** The whole game as of `message`, or null when it can't be rebuilt from what is kept. */
    rebuild(message) {
        if (message.kind === 'full') {
            this.games.set(message.gameId, { seq: message.seq, json: JSON.stringify(message.game) });
            return message.game;
        }
        const stream = this.games.get(message.gameId);
        if (!stream || !('json' in stream) || stream.seq !== message.baseSeq)
            return null;
        try {
            const game = applyJsonPatch(JSON.parse(stream.json), message.patch);
            this.games.set(message.gameId, { seq: message.seq, json: JSON.stringify(game) });
            return game;
        }
        catch {
            return null;
        }
    }
}
